import { useState } from "react";
import { ActivityIndicator } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Banner, Button } from "@visioni/a3s-ui/components";
import {
  defaultTimeZone,
  incidentsRecomputePeriod,
  useIncidentsRecomputes,
  useStartIncidentsRecompute,
  type IncidentsRecomputeRun,
  type StartIncidentsRecomputeRequest,
} from "@visioni/a3s-monitoring/app";
import { Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { formatInstant } from "../format";
import { recomputeProgress } from "../recompute-view";
import { CursorPagerBar } from "../components/CursorPagerBar";
import { ActivationDateRangeField, type DayRangeValues } from "../components/ActivationDateRangeField";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { TimeZoneField } from "../components/TimeZoneField";

/** How long the history keeps refreshing after a start, waiting for the run's record to appear. */
const AWAIT_RECORD_MS = 2 * 60_000;

const REFRESH_ICON_SIZE = 16;
/** Reserved whether or not the spinner is showing, so the button never moves. */
const REFRESH_SPINNER_SLOT = 20;

const viewerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function RecomputeIncidentsScreen() {
  const [pollUntil, setPollUntil] = useState<number | undefined>(undefined);

  return (
    <PatriotToolsPage
      title="Recompute incidents"
      subtitle="Counts genuine incidents again over whole days, from the Patriot records a3s holds now. Use it after a backfill, or once the counting rule changes. Each day's numbers are replaced, so what was reported for them changes."
    >
      <FeatureGate
        flag={FEATURE_FLAGS.MONITORING_INCIDENT_RECOMPUTE}
        fallback={
          <Banner
            variant="info"
            title="Recomputing incidents isn't enabled for you"
            message="Ask engineering to turn on monitoring_incident_recompute if you need it."
          />
        }
      >
        <View gap="3xl">
          <View style={styles.raised}>
            <RecomputeForm onStarted={() => setPollUntil(Date.now() + AWAIT_RECORD_MS)} />
          </View>
          <RecomputeHistory pollUntil={pollUntil} />
        </View>
      </FeatureGate>
    </PatriotToolsPage>
  );
}

function RecomputeForm({ onStarted }: { onStarted: () => void }) {
  const recompute = useStartIncidentsRecompute();
  const [days, setDays] = useState<DayRangeValues>({});
  // A recompute has no site, so Default is always the viewer's own zone.
  const [fallback] = useState(() => defaultTimeZone({ viewerTimeZone: viewerTimeZone() }));
  const [timeZone, setTimeZone] = useState(fallback.timeZone);
  const [confirmed, setConfirmed] = useState<StartIncidentsRecomputeRequest | undefined>(undefined);

  const { request, error } = incidentsRecomputePeriod(days, timeZone, new Date());
  // Changing the days after asking to start drops the check, so what starts is always what was shown.
  const confirming = request !== undefined && JSON.stringify(confirmed) === JSON.stringify(request);

  const start = () => {
    if (!request) return;
    recompute.mutate(request, {
      onSuccess: () => {
        showSuccessToast({
          message: "Recompute started",
          description: `${periodLabel(request, timeZone)}. It appears in the list below shortly.`,
        });
        setConfirmed(undefined);
        onStarted();
      },
      onError: (failure) => showErrorToast({ error: failure }),
    });
  };

  return (
    <View gap="md" style={styles.form}>
      {/* Stacked above the date range so its list opens over the fields below. */}
      <View style={styles.zone}>
        <TimeZoneField
          hasSiteFilter={false}
          value={timeZone}
          fallback={fallback}
          at={request === undefined ? new Date() : new Date(request.since)}
          onChange={setTimeZone}
        />
      </View>
      <View style={styles.raised}>
        <ActivationDateRangeField value={days} onChange={setDays} placeholder="Choose the days to recompute" />
      </View>
      {error !== undefined && (
        <Text textStyle="xs/regular" style={styles.error}>
          {error}
        </Text>
      )}
      <Text textStyle="xs/regular" color="textMuted">
        Whole days in the timezone above. Days are counted one after another, so a month takes roughly an hour.
      </Text>

      {confirming ?
        <View gap="md" style={styles.confirmation}>
          <Text textStyle="sm/semibold" color="text">
            Recompute these days?
          </Text>
          <Text textStyle="sm/regular" color="text">
            {`${periodLabel(request, timeZone)}. The genuine incidents already counted for them are replaced.`}
          </Text>
          <View flexDirection="row" gap="md">
            <Button label="Recompute" size="sm" onPress={start} loading={recompute.isPending} loadingText="Starting…" />
            <Button
              label="Cancel"
              variant="secondary"
              size="sm"
              onPress={() => setConfirmed(undefined)}
              disabled={recompute.isPending}
            />
          </View>
        </View>
      : <View flexDirection="row" gap="md">
          <Button label="Recompute" size="sm" onPress={() => setConfirmed(request)} disabled={!request} />
          <Button
            label="Reset"
            variant="secondary"
            size="sm"
            onPress={() => {
              setDays({});
              setTimeZone(fallback.timeZone);
            }}
            disabled={days.from === undefined && days.to === undefined && timeZone === fallback.timeZone}
          />
        </View>
      }
    </View>
  );
}

function RecomputeHistory({ pollUntil }: { pollUntil: number | undefined }) {
  const runs = useIncidentsRecomputes({ pollUntil });
  const { theme } = useUnistyles();

  return (
    <View gap="md">
      <View flexDirection="row" alignItems="center" justifyContent="space-between" gap="xl">
        <View gap="xs">
          <Text textStyle="sm/semibold" color="text">
            Recomputes, newest first
          </Text>
          {runs.fetchedAt !== undefined && (
            <Text textStyle="xs/regular" color="textMuted">
              {`Fetched at ${formatMoment(new Date(runs.fetchedAt).toISOString())}`}
            </Text>
          )}
        </View>
        {/* The list does not poll a running recompute, so this is how its progress is followed. */}
        <View flexDirection="row" alignItems="center" gap="md">
          <View style={{ width: REFRESH_SPINNER_SLOT }} alignItems="center">
            {runs.isFetching && <ActivityIndicator size="small" color={theme.colors.textMuted} />}
          </View>
          <Button
            label="Refresh"
            variant="secondary"
            size="sm"
            onPress={runs.refetch}
            disabled={runs.isFetching}
            iconLeft={<Icon name="refresh" size={REFRESH_ICON_SIZE} color={theme.colors.text} />}
          />
        </View>
      </View>
      {runs.items === undefined ?
        <QueryNotice
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={runs.refetch}
          loadingLabel="Loading recomputes…"
          errorTitle="Could not load recomputes"
        />
      : runs.items.length === 0 && !runs.pager.canGoBack ?
        <Text textStyle="sm/regular" color="textMuted">
          No recomputes yet.
        </Text>
      : <View gap="md">
          <View style={styles.card}>
            {runs.items.map((run, index) => (
              <RecomputeRunRow key={run.runId} run={run} first={index === 0} />
            ))}
          </View>
          <CursorPagerBar
            pager={runs.pager}
            nextCursor={runs.nextCursor}
            isFetching={runs.isFetching}
            summary={`${String(runs.items.length)} recomputes`}
          />
        </View>
      }
    </View>
  );
}

function RecomputeRunRow({ run, first }: { run: IncidentsRecomputeRun; first: boolean }) {
  const progress = recomputeProgress(run, formatMoment);
  return (
    <View gap="xs" style={[styles.row, first && styles.firstRow]}>
      <Text textStyle="xs/regular" color="textMuted">
        {`${formatMoment(run.startedAt)} · ${run.requestedByEmail ?? run.requestedBy ?? "Started outside Monitoring"}`}
      </Text>
      <Text textStyle="sm/regular" color="text">
        {periodLabel(run)}
      </Text>
      <Text textStyle="xs/regular" {...(progress.failed ? { style: styles.error } : { color: "textMuted" })}>
        {progress.summary}
      </Text>
      {progress.lines.map((line) => (
        <Text key={line} textStyle="xs/regular" style={styles.error}>
          {line}
        </Text>
      ))}
    </View>
  );
}

/** In `timeZone` when given, so a period being confirmed reads back in the zone its days were chosen in. */
const periodLabel = ({ since, until }: { readonly since: string; readonly until: string }, timeZone?: string) => {
  const format = timeZone === undefined ? formatMoment : (iso: string) => formatInstant(iso, timeZone);
  return `${format(since)} to ${format(until)}`;
};

const formatMoment = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const styles = StyleSheet.create((theme) => ({
  form: { maxWidth: 480 },
  // A dropdown opens over the siblings after it only when its ancestors stack above them.
  raised: { zIndex: 1 },
  zone: { zIndex: 2 },
  error: { color: theme.colors.status.error },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  firstRow: { borderTopWidth: 0 },
  confirmation: {
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
}));
